import {
  Box, Button, CircularProgress, Divider, Stack, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { Trans } from '@lingui/react';
import { useQueryClient } from 'react-query';
import {
  PourDto,
  useCreatePourMutation,
  useDeletePourMutation,
  useExportPourShapesDufMutation,
  useMineModelRheologyDataListQuery,
  usePoursQuery
} from 'providers/api';
import useApi from 'providers/api/useApi';
import { usePage } from 'providers/page';
import { useConfirmation } from 'providers/confirm';
import { useScenario } from 'modules/project/providers/scenario';
import { i18n } from 'i18n';
import React from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { resolveTemplate } from 'utils';
import StopeFillPourForm from 'modules/project/StopeFillPourForm/StopeFillPourForm';
import StopePourListView from 'modules/project/StopeFillPage/StopePourListView';
import {
  ROUTE_PATH as STOPE_NOTE_ROUTE_PATH,
  ROUTE_PARAM_IDENTIFIER as stopeNoteProjectParam,
  STOPE_PARAM_IDENTIFIER as stopeNoteStopeParam
} from 'modules/project/StopeNotePage/constants';
import {
  defaultFillAnchor,
  FillAnchor,
  LEGACY_BEACH_ANGLE_DEGREES,
  nextSliceBase,
  resolveNewPourBeachAngle,
  sortPoursChronologically,
  walkStopeFill
} from 'modules/project/StopeFillPage/Three/pour';
import { Model } from '../../hooks/useModelState';
import { SceneProps, SceneSideDrawerRoute, navigateToScene } from '../scene-utils';
import ModelDrawer from '../../ModelDrawer';
import { getStopeFillSceneState, subscribeToStopeFillSceneState, updateStopeFillSceneState } from './stopeFillSceneState';
import ScenarioPourWarning from './ScenarioPourWarning';
import StopePresetPours from './StopePresetPours';
import buildPourSolidsExport from './exportPourSolidsDuf';

interface StopeFillDrawerContentProps {
  model: Model;
}

const StopeFillDrawerContent = ({ model }: StopeFillDrawerContentProps) => {
  const stopeId = model.selectedStope?.data?.stopeId;
  const projectId = model.project?.entityId;

  const navigate = useNavigate();
  const [pourHeight, setPourHeight] = React.useState<number | null>(null);
  const [mode, setMode] = React.useState<'list' | 'create'>('list');
  const [updatingVolumes, setUpdatingVolumes] = React.useState(false);
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  const api = useApi();
  const queryClient = useQueryClient();
  const prevPipePositionRef = React.useRef<{ x: number; y: number } | null>(null);
  const {
    actions: {
      setUndoActionOverride,
      clearUndoActionOverride,
    },
  } = usePage();
  const createdPourUndoStackRef = React.useRef<string[]>([]);
  const deletePourMutation = useDeletePourMutation();

  const publishCreatePourUndoAction = React.useCallback(() => {
    const hasUndoItems = createdPourUndoStackRef.current.length > 0;
    if (!hasUndoItems) {
      clearUndoActionOverride();
      return;
    }

    const handleUndoCreate = () => {
      const latestCreatedPourId = createdPourUndoStackRef.current[createdPourUndoStackRef.current.length - 1];
      if (!latestCreatedPourId) {
        clearUndoActionOverride();
        return;
      }

      setUndoActionOverride((current) => (current ? {
        ...current,
        enabled: false,
        loading: true,
      } : current));

      deletePourMutation.mutate({ entityId: latestCreatedPourId } as PourDto, {
        onSuccess: () => {
          const currentTop = createdPourUndoStackRef.current[createdPourUndoStackRef.current.length - 1];
          if (currentTop === latestCreatedPourId) {
            createdPourUndoStackRef.current.pop();
          } else {
            createdPourUndoStackRef.current = createdPourUndoStackRef.current
              .filter((pourId) => pourId !== latestCreatedPourId);
          }

          publishCreatePourUndoAction();
        },
        onError: () => {
          publishCreatePourUndoAction();
        },
      });
    };

    setUndoActionOverride({
      enabled: true,
      loading: false,
      onClick: handleUndoCreate,
    });
  }, [clearUndoActionOverride, deletePourMutation, setUndoActionOverride]);

  React.useEffect(() => {
    const unsubscribe = subscribeToStopeFillSceneState(() => forceUpdate());
    return unsubscribe;
  }, []);

  React.useEffect(() => () => {
    createdPourUndoStackRef.current = [];
    clearUndoActionOverride();
  }, [clearUndoActionOverride]);

  const sceneState = getStopeFillSceneState();
  const {
    originalStopeModel, fillSurfaces, cumulativeVolume, pipePosition, inputMode, currentVolume,
  } = sceneState;

  // The project default, already collapsed to a number so the form can show it as a placeholder. Null on
  // the project means "use the legacy 1.25" - the same fallback the backend applies.
  const projectDefaultBeachAngle = model.project?.defaultPasteBeachAngleDegrees ?? LEGACY_BEACH_ANGLE_DEGREES;

  const { activeScenarioId } = useScenario();
  const { data: poursData, refetch: refetchPours } = usePoursQuery(
    { projectId: projectId!, stopeId: stopeId!, scenarioId: activeScenarioId ?? undefined, includeRockFill: true },
    { enabled: !!projectId && !!stopeId, staleTime: 120000000 },
  );

  const createMutation = useCreatePourMutation({
    onSuccess: (createdPourId) => {
      if (createdPourId) {
        createdPourUndoStackRef.current.push(createdPourId);
        publishCreatePourUndoAction();
      }
    },
  });

  const { data: rheologyDataSummaryList, isLoading: rheologyDataSummaryListIsLoading } = useMineModelRheologyDataListQuery(
    { mineModelId: projectId!, rheologyDataSetIds: [] },
    { enabled: !!projectId, staleTime: 120000000 },
  );

  // The per-pour override currently typed into the form. The drawer recomputes on its own for a pipe move
  // and for the model finishing loading, and those paths have no access to formik state - without this
  // they would silently re-derive on the project-default curve and republish the preview with no override,
  // reverting the rendered solid while the form field still showed the override. Cleared wherever the
  // create form is torn down, since formik resets its own field at the same moment.
  const [beachAngleOverride, setBeachAngleOverride] = React.useState<number | undefined>(undefined);

  const handlePreviewPour = React.useCallback((volume: number, height?: number, beachAngleDegrees?: number) => {
    setBeachAngleOverride(beachAngleDegrees);
    updateStopeFillSceneState({ previewPour: { volume, height, beachAngleDegrees } });
  }, []);

  // Everything the create form carried, dropped together. pourHeight is drawer state rather than formik
  // state, so it outlives the form: leaving it set means the next pour opens pre-filled with the last
  // one's height and, because the field never changes, publishes no preview until it is overtyped.
  const resetPourEntry = React.useCallback(() => {
    updateStopeFillSceneState({ previewPour: null });
    setBeachAngleOverride(undefined);
    setPourHeight(null);
  }, []);

  // The base the NEXT pour will be built on, derived exactly as walkStopeFill derives it when the pour is
  // committed - so the figure the form shows describes the same solid the pour ends up as. Both directions
  // are O(1) curve lookups, which is what removes the 114-277 ms lag the old CSG search added per keystroke.
  const resolveNextSlice = React.useCallback((beachAngleDegrees?: number) => {
    const current = getStopeFillSceneState();
    const pristine = current.originalStopeModel;
    if (!pristine) return null;

    const anchor: FillAnchor = current.pipePosition
      ? { x: current.pipePosition.x, z: current.pipePosition.y }
      : defaultFillAnchor(pristine);

    // fillSurfaces is empty until a walk has stacked at least one pour, which is exactly the condition
    // for the next slice being pour 1 and taking the discharge-floor datum.
    // Same chain the created pour will use: form override -> project default -> legacy constant.
    const slopeDegrees = resolveNewPourBeachAngle(beachAngleDegrees, projectDefaultBeachAngle);

    return nextSliceBase(pristine, anchor, slopeDegrees, current.fillSurfaces);
  }, [projectDefaultBeachAngle]);

  const computeVolumeFromHeight = React.useCallback((height: number, beachAngleDegrees?: number) => {
    if (!Number.isFinite(height) || height <= 0) return 0;
    const slice = resolveNextSlice(beachAngleDegrees);
    if (!slice) return 0;

    try {
      return Math.max(0, slice.curve.volumeAt(slice.datum + height) - slice.baseVolume);
    } catch {
      return 0;
    }
  }, [resolveNextSlice, originalStopeModel, pipePosition, fillSurfaces, cumulativeVolume]);

  const computeHeightFromVolume = React.useCallback((volume: number, beachAngleDegrees?: number) => {
    if (!Number.isFinite(volume) || volume <= 0) return 0;
    const slice = resolveNextSlice(beachAngleDegrees);
    if (!slice) return 0;

    try {
      return Math.max(0, slice.curve.levelAt(slice.baseVolume + volume) - slice.datum);
    } catch {
      return 0;
    }
  }, [resolveNextSlice, originalStopeModel, pipePosition, fillSurfaces, cumulativeVolume]);

  const modelWasNullRef = React.useRef<boolean>(!originalStopeModel);

  React.useEffect(() => {
    const prevPos = prevPipePositionRef.current;

    const positionChanged = prevPos
      && pipePosition
      && (prevPos.x !== pipePosition.x || prevPos.y !== pipePosition.y);

    if (!positionChanged) {
      prevPipePositionRef.current = pipePosition;
      return;
    }

    if (inputMode === 'height' && pourHeight && pourHeight > 0) {
      const newVolume = computeVolumeFromHeight(pourHeight, beachAngleOverride);
      handlePreviewPour(newVolume, pourHeight, beachAngleOverride);
    } else if (inputMode === 'volume' && currentVolume && currentVolume > 0) {
      const newHeight = computeHeightFromVolume(currentVolume, beachAngleOverride);
      setPourHeight(newHeight);
      handlePreviewPour(currentVolume, newHeight, beachAngleOverride);
    }

    prevPipePositionRef.current = pipePosition;
  }, [pipePosition, pourHeight, inputMode, currentVolume, computeVolumeFromHeight, computeHeightFromVolume, handlePreviewPour, setPourHeight, beachAngleOverride]);

  React.useEffect(() => {
    const modelJustLoaded = modelWasNullRef.current && originalStopeModel;
    modelWasNullRef.current = !originalStopeModel;

    if (modelJustLoaded && pourHeight && pourHeight > 0) {
      const newVolume = computeVolumeFromHeight(pourHeight, beachAngleOverride);
      handlePreviewPour(newVolume, pourHeight, beachAngleOverride);
    }
  }, [originalStopeModel, pourHeight, computeVolumeFromHeight, handlePreviewPour, beachAngleOverride]);

  const handlePourSelect = (pour: any) => {
    updateStopeFillSceneState({ selectedPourId: pour?.entityId });
  };

  const exportPourShapesDufMutation = useExportPourShapesDufMutation(projectId ?? '');

  // Rebuilds the fill solids exactly as drawn (walkStopeFill against the original stope), maps them
  // back to mine-grid coordinates and downloads a DUF with one solid per pour for use in CAD.
  const handleExportSolids = React.useCallback(async () => {
    const { originalStopeModel: exportStope, stopeSourceBounds } = getStopeFillSceneState();
    const pours = poursData ?? [];
    if (!exportStope || !stopeSourceBounds || pours.length === 0 || !projectId || !stopeId) return;

    const stopeName = model.selectedStope?.data?.stopeName || 'stope';

    let exportData;
    try {
      exportData = buildPourSolidsExport(exportStope, stopeSourceBounds, pours, stopeName);
    } catch {
      return;
    }
    if (!exportData) return;

    const file = await exportPourShapesDufMutation.mutateAsync({ mineModelId: projectId, stopeId, exportData });
    const url = window.URL.createObjectURL(file.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.fileName ?? `${stopeName}-pour-solids.duf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, [poursData, projectId, stopeId, model.selectedStope, exportPourShapesDufMutation]);

  const confirm = useConfirmation();
  const handleDeletePour = React.useCallback((pour: PourDto) => {
    confirm({
      variant: 'danger',
      description: i18n._('This action will permanently delete the pour. Are you sure you want to continue?'),
    }).then(() => {
      deletePourMutation.mutate(pour, {
        onSuccess: () => {
          createdPourUndoStackRef.current = createdPourUndoStackRef.current.filter((id) => id !== pour.entityId);
          publishCreatePourUndoAction();
          refetchPours();
        },
      });
    });
  }, [confirm, deletePourMutation, publishCreatePourUndoAction, refetchPours]);

  // Recomputes existing pour volumes against the current stope shape after the solid has been edited. Each
  // pour is re-stacked at the angle IT was planned at, never at the current project default - otherwise a
  // project-settings edit would turn this button into a silent rewrite of every historical pour. Uses
  // the SAME stacking the fill view renders (walkStopeFill: one cone per pour, the fill surface being
  // their pointwise maximum) so the written volumes match what is drawn. step.sliceVolume telescopes out
  // of the exact integral rather than being measured off the CSG shell, so it cannot drift. A pour keeps its definition: a
  // height-defined pour keeps its height and has its volume re-derived from the new shape; a volume-defined
  // pour keeps its volume unchanged. Only materially-changed volumes are PATCHed.
  const handleUpdateVolumes = React.useCallback(async () => {
    const base = getStopeFillSceneState().originalStopeModel;
    const pours = poursData ?? [];
    if (!base || pours.length === 0) return;

    setUpdatingVolumes(true);
    try {
      const { pipePosition: currentPipePosition } = getStopeFillSceneState();
      const defaultAnchor: FillAnchor = currentPipePosition
        ? { x: currentPipePosition.x, z: currentPipePosition.y }
        : defaultFillAnchor(base);

      const updates: Array<{ entityId: string; volume: number }> = [];
      walkStopeFill(base, sortPoursChronologically(pours), defaultAnchor, (step) => {
        // Volume-defined pours keep their stored volume; only re-derive height-defined ones.
        if (step.pour.pourHeight == null) return;
        if (step.sliceVolume > 0 && Math.abs(step.sliceVolume - step.pour.volume) > 0.5) {
          updates.push({ entityId: step.pour.entityId, volume: step.sliceVolume });
        }
      });

      await updates.reduce(async (previous, update) => {
        await previous;
        await api.pours.updatePourVolume(update.entityId, { volume: update.volume });
        return undefined;
      }, Promise.resolve<undefined>(undefined));

      await queryClient.invalidateQueries('pours', { exact: false });
      await queryClient.invalidateQueries('paged_pours', { exact: false });
      await queryClient.invalidateQueries('stopeBackfills', { exact: false });
      await queryClient.invalidateQueries('stopeBackfill', { exact: false });
      refetchPours();
    } finally {
      setUpdatingVolumes(false);
    }
  }, [api, poursData, queryClient, refetchPours]);

  if (!stopeId || !projectId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>{i18n._('No stope selected')}</Typography>
      </Box>
    );
  }

  if (mode === 'create') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
        <ScenarioPourWarning />
        <Box sx={{ mb: 1 }}>
          <Button
            size="small"
            color="secondary"
            onClick={() => {
              resetPourEntry();
              setMode('list');
            }}
          >
            <Trans id="Back to pours">Back to pours</Trans>
          </Button>
        </Box>
        <StopeFillPourForm
          projectId={projectId}
          stopeId={stopeId}
          unitSystemPreference={model.project.unitSystemPreference}
          mutation={createMutation}
          customUnitFields={model.project?.customUnitFields ?? []}
          fillType={model.project.fillType}
          onPreviewPour={handlePreviewPour}
          onRecipeChange={() => { }}
          onCreateCompleted={() => {
            resetPourEntry();
            refetchPours();
            setMode('list');
          }}
          pourHeight={pourHeight}
          setPourHeight={setPourHeight}
          pipePosition={sceneState.pipePosition}
          computeVolumeFromHeight={computeVolumeFromHeight}
          computeHeightFromVolume={computeHeightFromVolume}
          projectBeachAngleDegrees={projectDefaultBeachAngle}
        />
      </Box>
    );
  }

  const hasPours = (poursData?.length ?? 0) > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
      <ScenarioPourWarning />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => setMode('create')}
        >
          <Trans id="Create Pour">Create Pour</Trans>
        </Button>
      </Stack>

      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          <Trans id="Pour Presets">Pour Presets</Trans>
        </Typography>
        <StopePresetPours
          model={model}
          projectId={projectId}
          stopeId={stopeId}
          existingPours={poursData ?? []}
          onChanged={refetchPours}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Button
        fullWidth
        variant="contained"
        color="secondary"
        startIcon={<NoteAddOutlinedIcon />}
        disabled={!hasPours}
        sx={{ mb: 2 }}
        onClick={() => navigate({
          pathname: resolveTemplate(STOPE_NOTE_ROUTE_PATH, {
            [stopeNoteProjectParam]: projectId,
            [stopeNoteStopeParam]: stopeId,
          }),
        })}
      >
        <Trans id="Stope Approval">Stope Approval</Trans>
      </Button>

      <Button
        fullWidth
        variant="outlined"
        color="secondary"
        startIcon={exportPourShapesDufMutation.isLoading
          ? <CircularProgress size={16} />
          : <FileDownloadOutlinedIcon />}
        disabled={!hasPours || !sceneState.originalStopeModel || !sceneState.stopeSourceBounds || exportPourShapesDufMutation.isLoading}
        sx={{ mb: 2 }}
        onClick={handleExportSolids}
      >
        <Trans id="Export Solids (DUF)">Export Solids (DUF)</Trans>
      </Button>

      <Box>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1,
        }}
        >
          <Typography variant="subtitle2">
            <Trans id="Existing Pours">Existing Pours</Trans>
          </Typography>
          {hasPours && (
            <Button
              size="small"
              color="secondary"
              disabled={updatingVolumes}
              startIcon={updatingVolumes ? <CircularProgress size={14} /> : undefined}
              onClick={handleUpdateVolumes}
            >
              <Trans id="Update Volumes">Update Volumes</Trans>
            </Button>
          )}
        </Box>
        <StopePourListView
          backFillPlantNodeId={model.activeBackfillPlant?.id}
          project={model.project}
          stopeId={stopeId}
          rheologyDataSummaryListIsLoading={rheologyDataSummaryListIsLoading}
          rheologyDataSummaryList={rheologyDataSummaryList ?? []}
          pourData={poursData ?? []}
          pourIdSelected={sceneState.selectedPourId}
          onPourSelect={handlePourSelect}
          onDeletePour={handleDeletePour}
        />
      </Box>
    </Box>
  );
};

const stopeFillSceneSideDrawerRoutes = (
  mdl: Model,
  currentDrawerState: boolean,
  setDrawerStateCallback: (isOpen: boolean) => void,
  sceneProps: SceneProps,
): SceneSideDrawerRoute[] => [
    {
      path: '*',
      isOpen: currentDrawerState,
      setIsOpen: setDrawerStateCallback,
      backNavText: 'Stope', // msgid; ModelDrawer translates it
      onBack: () => {
        navigateToScene('STOPE_SELECTED', {
          model: mdl,
          context: sceneProps.context,
          stopeId: mdl.selectedStope?.data?.stopeId,
        });
      },
      component: mdl && (
        <StopeFillDrawerContent model={mdl} />
      ),
    },
  ];

const sideDrawerContent = (
  sceneProps: SceneProps,
  mdl: Model,
): JSX.Element => {
  const routes = stopeFillSceneSideDrawerRoutes(
    mdl,
    sceneProps.context.state.drawerOpen,
    sceneProps.context.actions.setDrawerOpen,
    sceneProps,
  );

  return (
    <Routes>
      {routes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={(
            <ModelDrawer
              isOpen={route.isOpen}
              setIsOpen={route.setIsOpen}
              backNavText={route.backNavText}
              onBack={route.onBack}
            >
              {route.component}
            </ModelDrawer>
          )}
        />
      ))}
    </Routes>
  );
};

export default sideDrawerContent;
